<template>
	<main class="teacher-detail-page">
		<Teleport to="body">
			<nav class="teacher-detail-page__breadcrumb">
				<Breadcrumb
					:name="displayTeacherName"
					back-name="common.breadcrumb.tutor"
					back-path="/seer/master-fortune"
				/>
			</nav>
		</Teleport>
		<div class="teacher-detail-page__container" :aria-busy="pending">
			<PageSkeleton v-if="showPageSkeleton" />
			<TeacherInfo v-else :data="teacherInfo" />

			<section v-if="!showPageSkeleton" class="teacher-detail-content">
				<nav
					v-if="!isAreaTeacher"
					class="teacher-detail-content__tabs"
					:aria-label="t(`${I18N_NS}.sections.media`)"
				>
					<button
						v-for="section in visibleSections"
						:key="section.key"
						type="button"
						class="teacher-detail-content__tab"
						@click="scrollToSection(section.key)"
					>
						{{ section.label }}
					</button>
				</nav>

				<section
					v-if="consultService.length"
					ref="consultSectionRef"
					class="teacher-detail-content__section"
				>
					<h2 class="sr-only">{{ t(`${I18N_NS}.consultList`) }}</h2>
					<ConsultService :data="consultService" :variant="detailVariant" />
				</section>

				<section
					v-if="fahuiService.length"
					ref="solutionSectionRef"
					class="teacher-detail-content__section"
				>
					<h2 class="sr-only">{{ t(`${I18N_NS}.solutionList`) }}</h2>
					<FahuiService :data="fahuiService" :variant="detailVariant" />
				</section>

				<section
					v-if="hasMediaContent"
					ref="mediaSectionRef"
					class="teacher-detail-content__section"
				>
					<h2 class="sr-only">{{ t(`${I18N_NS}.mediaList`) }}</h2>
					<MediaContent :data="mediaContent" />
				</section>

				<section
					v-show="hasEvaluateContent"
					ref="evaluateSectionRef"
					class="teacher-detail-content__section"
				>
					<h2 class="sr-only">{{ t(`${I18N_NS}.evaluateList`) }}</h2>
					<ClientOnly>
						<UserEvaluate
							:teacher-id="teacherId"
							@has-content-change="hasEvaluateContent = $event"
						/>
					</ClientOnly>
				</section>
			</section>
		</div>
	</main>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import Breadcrumb from "~/components/pc/breadcrumb/index.vue";
import { useI18n } from "~/composables/useI18n";
import ConsultService from "./components/consult-service/index.vue";
import FahuiService from "./components/fahui-service/index.vue";
import MediaContent from "./components/media-content/index.vue";
import TeacherInfo from "./components/teacher-info/index.vue";
import UserEvaluate from "./components/user-evaluate/index.vue";
import PageSkeleton from "./components/page-skeleton/index.vue";
import { useTeacherDetailPageData } from "@variants/seer/teacher-detail/composables/useTeacherDetailPageData";
import {
	provideMentorDetailTracking,
	resolveMentorDetailTabName,
	useMentorDetailTracking,
} from "@variants/seer/teacher-detail/composables/useMentorDetailTracking";

const I18N_NS = "teacherDetail.pc";
const { t, locale } = useI18n();

const route = useRoute();
const teacherId = route.params.id;

const { teacherInfo, consultService, fahuiService, mediaContent, pending } =
	useTeacherDetailPageData(teacherId);

const showPageSkeleton = computed(
	() => pending.value && !Object.keys(teacherInfo.value || {}).length
);

const isAreaTeacher = computed(() => Number(teacherInfo.value?.area_type) === 10);
const detailVariant = computed(() => (isAreaTeacher.value ? "area" : "default"));

const tracking = provideMentorDetailTracking(
	useMentorDetailTracking({
		deviceType: "pc",
		mentorId: teacherId,
		areaType: computed(() => teacherInfo.value?.area_type),
	})
);
const { track, normalizeMentorRating, getConsultationCountBucket } = tracking;

const pageViewFired = ref(false);
watch(
	teacherInfo,
	info => {
		if (!import.meta.client || pageViewFired.value) return;
		if (!info || !Object.keys(info).length) return;
		pageViewFired.value = true;
		import("~/utils/firebase").then(({ logEvent }) => {
			logEvent("site_masterpage_uv");
		});
		track("mentor_detail_page_view", {
			rating: normalizeMentorRating(info.score),
			consultation_count_bucket: getConsultationCountBucket(info.answers),
		});
	},
	{ immediate: true }
);
const displayTeacherName = computed(
	() =>
		(locale.value === "en" ? teacherInfo.value?.nickname_en : teacherInfo.value?.nickname) ||
		teacherInfo.value?.nickname ||
		""
);

const consultSectionRef = ref(null);
const solutionSectionRef = ref(null);
const mediaSectionRef = ref(null);
const evaluateSectionRef = ref(null);
const hasEvaluateContent = ref(false);

const hasMediaContent = computed(
	() =>
		Boolean(mediaContent.value?.horizVideo?.length) ||
		Boolean(mediaContent.value?.vertVideo?.length) ||
		Boolean(mediaContent.value?.articleList?.length)
);

const visibleSections = computed(() =>
	[
		{
			key: "consult",
			label: t(`${I18N_NS}.sections.consultService`),
			visible: consultService.value.length > 0,
		},
		{
			key: "solution",
			label: t(`${I18N_NS}.sections.solutionService`),
			visible: fahuiService.value.length > 0,
		},
		{
			key: "media",
			label: t(`${I18N_NS}.sections.media`),
			visible: hasMediaContent.value,
		},
		{
			key: "evaluate",
			label: t(`${I18N_NS}.sections.userEvaluate`),
			visible: hasEvaluateContent.value,
		},
	].filter(item => item.visible)
);

const sectionRefs = {
	consult: consultSectionRef,
	solution: solutionSectionRef,
	media: mediaSectionRef,
	evaluate: evaluateSectionRef,
};

function scrollToSection(key) {
	track("mentor_detail_tab_click", {
		tab_name: resolveMentorDetailTabName(key),
		previous_tab: resolveMentorDetailTabName(visibleSections.value[0]?.key),
	});
	const target = sectionRefs[key]?.value;
	if (!target) return;
	target.scrollIntoView({
		behavior: "smooth",
		block: "start",
	});
}
</script>

<style lang="scss" scoped>
.teacher-detail-page {
	padding-bottom: 120px;
	background: #ffffff;
	font-family: "Source Han Serif CN", "Source Han Serif CN";

	&__container {
		width: min(1200px, calc(100% - 48px));
		margin: 0 auto;
	}

	&__breadcrumb {
		position: fixed;
		left: 50%;
		top: 80px;
		z-index: 90;
		width: min(1200px, calc(100% - 48px));
		transform: translateX(-50%);
		padding: 32px 0 24px;
		background: #ffffff;
		box-shadow: 0 8px 18px rgba(255, 255, 255, 0.96);
	}
}

.teacher-detail-page__container::before {
	content: "";
	display: block;
	height: 88px;
}

.teacher-detail-content {
	margin-top: 32px;
	padding: 40px 48px 52px;
	background: #ffffff;
	border: 1px solid #eeeeee;
	border-radius: 8px;
	box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);

	&__tabs {
		display: flex;
		flex-wrap: wrap;
		gap: 16px;
		margin-bottom: 32px;
	}

	&__tab {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		min-height: 40px;
		padding: 8px 20px;
		border: 1px solid #e5e5e5;
		border-radius: 8px;
		background: #ffffff;
		color: #999;
		font-size: 18px;
		font-weight: 500;
		line-height: 1.4;
		cursor: pointer;
		white-space: nowrap;
		transition:
			color 0.2s ease,
			border-color 0.2s ease,
			background-color 0.2s ease;

		&:first-child {
			border-color: #333;
			color: #333;
			font-weight: 500;
		}
	}

	&__section {
		scroll-margin-top: 96px;

		& + & {
			margin-top: 44px;
		}
	}
}

.sr-only {
	position: absolute;
	width: 1px;
	height: 1px;
	padding: 0;
	margin: -1px;
	overflow: hidden;
	clip: rect(0, 0, 0, 0);
	white-space: nowrap;
	border: 0;
}

@media (max-width: 1180px) {
	.teacher-detail-content {
		padding: 32px 32px 44px;

		&__tabs {
			gap: 12px;
		}
	}
}

@media (max-width: 1024px) {
	.teacher-detail-page {
		&__container {
			width: min(100% - 32px, 1200px);
		}
	}

	.teacher-detail-content {
		padding: 28px 24px 36px;

		&__tabs {
			gap: 12px;
		}
	}
}

@media (max-width: 900px) {
	.teacher-detail-page {
		padding-bottom: 80px;

		&__breadcrumb {
			top: 96px;
			width: min(100% - 32px, 1200px);
			padding: 20px 0 16px;
			overflow-x: auto;
			scrollbar-width: none;

			&::-webkit-scrollbar {
				display: none;
			}
		}
	}

	.teacher-detail-page__container::before {
		height: 72px;
	}

	.teacher-detail-content {
		margin-top: 24px;
		padding: 24px 20px 32px;

		&__tabs {
			flex-wrap: nowrap;
			gap: 12px;
			margin-bottom: 24px;
			overflow-x: auto;
			scrollbar-width: none;

			&::-webkit-scrollbar {
				display: none;
			}
		}

		&__tab {
			min-height: 36px;
			padding: 6px 16px;
			font-size: 14px;
		}
	}
}

@media (max-width: 640px) {
	.teacher-detail-page {
		&__container,
		&__breadcrumb {
			width: calc(100% - 24px);
		}
	}

	.teacher-detail-content {
		padding: 20px 16px 28px;
	}
}
</style>
